import * as React from 'react';
import { showGlobalModal } from 'core/components/GlobalModal';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import { testimonialsApi, Testimonial } from 'features/DigitalMarketing/pages/EditWebsiteContent/api/testimonialsApi';

export const useTestimonials = () => {
    const [testimonials, setTestimonials] = React.useState<Testimonial[]>([]);
    const [isLoadingTestimonials, setIsLoadingTestimonials] = React.useState(false);
    const [isDeletingTestimonial, setIsDeletingTestimonial] = React.useState(false);
    const [publishingTestimonialId, setPublishingTestimonialId] = React.useState<number | null>(null);
    const [pageIndex, setPageIndex] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalRecords, setTotalRecords] = React.useState(0);

    const fetchTestimonials = React.useCallback(async () => {
        setIsLoadingTestimonials(true);

        try {
            const response = await testimonialsApi.list(pageIndex, pageSize);
            setTestimonials(response.items);
            setTotalRecords(response.total);
        } catch (error: any) {
            showGlobalModal(getApiErrorMessage(error, 'Unable to fetch testimonials'), 'error');
            setTestimonials([]);
            setTotalRecords(0);
        } finally {
            setIsLoadingTestimonials(false);
        }
    }, [pageIndex, pageSize]);

    React.useEffect(() => {
        fetchTestimonials();
    }, [fetchTestimonials]);

    const handlePageSizeChange = React.useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setPageIndex(1);
    }, []);

    const deleteTestimonial = React.useCallback(async (testimonialId: number) => {
        const selectedTestimonial = testimonials.find(testimonial => testimonial.id === testimonialId);
        const parentFeedbackId = selectedTestimonial?.parentFeedbackId || testimonialId;

        setIsDeletingTestimonial(true);

        try {
            const response = await testimonialsApi.delete(parentFeedbackId);
            showGlobalModal(response?.data?.message || 'Testimonial deleted successfully');
            if (testimonials.length === 1 && pageIndex > 1) {
                setPageIndex(currentPage => Math.max(1, currentPage - 1));
            } else {
                await fetchTestimonials();
            }
            return true;
        } catch (error: any) {
            showGlobalModal(getApiErrorMessage(error, 'Unable to delete testimonial'), 'error');
            return false;
        } finally {
            setIsDeletingTestimonial(false);
        }
    }, [fetchTestimonials, pageIndex, testimonials]);

    const publishTestimonial = React.useCallback((testimonialId: number) => {
        const selectedTestimonial = testimonials.find(testimonial => testimonial.id === testimonialId);

        if (!selectedTestimonial || selectedTestimonial.status === 'Live') {
            return;
        }

        showGlobalModal(
            'Are you sure you want to publish this testimonial?',
            'warning',
            {
                showConfirm: true,
                confimLabel: 'Publish',
                cancelLabel: 'Cancel',
                onConfirm: async () => {
                    setPublishingTestimonialId(testimonialId);

                    try {
                        const response = await testimonialsApi.publish(selectedTestimonial.parentFeedbackId || testimonialId);
                        showGlobalModal(response?.data?.message || 'Published successfully');
                        await fetchTestimonials();
                    } catch (error: any) {
                        showGlobalModal(getApiErrorMessage(error, 'Unable to publish testimonial'), 'error');
                    } finally {
                        setPublishingTestimonialId(null);
                    }
                },
                onCancel: () => true,
            },
        );
    }, [fetchTestimonials, testimonials]);

    return {
        testimonials,
        pageIndex,
        pageSize,
        totalRecords,
        isLoadingTestimonials,
        isDeletingTestimonial,
        publishingTestimonialId,
        setPageIndex,
        setPageSize: handlePageSizeChange,
        deleteTestimonial,
        publishTestimonial,
    };
};
